<template>
  <div class="user-info-wrapper">
    <div v-if="user.customer_id" class="user-info-group">
      <div class="user-info-label">ID: {{ user.customer_id }}</div>
    </div>
    <div v-if="user.main_address" class="user-info-group">
      <div v-if="user.main_address.address_street" class="user-info-label">{{ user.main_address.address_street }}</div>
      <div v-if="user.main_address.address_street2" class="user-info-label">
        {{ user.main_address.address_street2 }}
      </div>
      <div v-if="address" class="user-info-label">{{ address }}</div>
      <div v-if="user.main_address.country?.name" class="user-info-label">
        {{ user.main_address.country.name }}
      </div>
    </div>
    <div class="user-info-group">
      <div v-if="user.main_address?.contact_phone" class="user-info-label">{{ user.main_address?.contact_phone }}</div>
      <div v-if="user.phone" class="user-info-label">{{ user.phone }}</div>
    </div>

    <div class="action-buttons">
      <button
        @click="onApproveBtn"
        :class="['btn', state.customer_status === CustomerStatus.APPROVED ? 'btn-warn-outline' : 'btn-blue-outline']">
        {{ approveBtnText }}
      </button>
      <button class="btn btn-blue-outline" @click="onMuteBtn">{{ muteBtnText }}</button>
      <button class="btn btn-blue stage-b-item" @click="onOpenChat">
        {{ trans("live.user_list.popup.buttons.message") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { IUserPopupData } from "@/models";
  import { computed, inject, reactive } from "vue";
  import { CustomerStatus } from "@/enum";
  import { trans } from "@/utils/trans";
  import apiClient from "@/services/api";
  import { useToast } from "primevue/usetoast";

  const toast = useToast();

  const emit = defineEmits(["closeForm", "changeTitle", "setModalHeightAuto"]);
  const props = defineProps<{ data: { user: IUserPopupData } }>();
  const auctionStore = useAuctionStore();
  const emitter = inject("emitter");

  const state = reactive({
    customer_status: props.data.user.customer_status,
    customer_is_muted: props.data.user.customer_is_muted,
    waitCSResponse: false,
    waitIMResponse: false
  });

  // eslint-disable-next-line no-undef
  const user = computed(() => {
    return props.data.user;
  });

  const address = computed(() => {
    const parts = [];
    let info = "";
    if (user?.value?.main_address?.address_state) {
      parts.push(user.value.main_address.address_state);
    }
    if (user?.value?.main_address?.address_zip) {
      parts.push(user.value.main_address.address_zip);
    }

    if (parts.length > 0) {
      info = `, ${parts.join(" ")}`;
    }

    return `${user?.value?.main_address?.address_city}${info}`;
  });

  const approveBtnText = computed(() => {
    return state.customer_status != CustomerStatus.APPROVED
      ? trans("live.user_list.popup.buttons.approve")
      : trans("live.user_list.popup.buttons.block");
  });

  const muteBtnText = computed(() => {
    return state.customer_is_muted
      ? trans("live.user_list.popup.buttons.unmute")
      : trans("live.user_list.popup.buttons.mute");
  });

  const onApproveBtn = () => {
    if (state.waitCSResponse) return;

    const href =
      auctionStore.routes[state.customer_status != CustomerStatus.APPROVED ? "live.users.approve" : "live.users.block"];
    state.waitCSResponse = true;
    apiClient
      .put(href, { userId: user.value.id })
      .then((response) => {
        if (response.data.status) {
          state.customer_status = response.data.customer_status;
          auctionStore.$patch((storeState) => {
            if (storeState.userData[user.value.id]) {
              storeState.userData[user.value.id].customer_status = state.customer_status;
            }
            const index = auctionStore.activeLot?.bidders.findIndex((item) => {
              return item.id === user.value.id;
            });
            if (index && index >= 0) {
              storeState.activeLot.bidders[index].customer_status = state.customer_status;
            }
          });
        }
        toast.add({
          severity: response.data.status ? "success" : "error",
          summary: trans("live.toast." + (response.data.status ? "success" : "error")),
          detail: response.data.message || trans("live.toast.unknown_error"),
          life: 6000
        });
      })
      .finally(() => (state.waitCSResponse = false));
  };

  const onMuteBtn = () => {
    if (state.waitIMResponse) return;

    const href = auctionStore.routes[state.customer_is_muted ? "live.users.unmute" : "live.users.mute"];
    state.waitIMResponse = true;
    apiClient
      .put(href, { userId: user.value.id })
      .then((response) => {
        if (response.data.status) {
          state.customer_is_muted = response.data.customer_is_muted;
          emitter?.emit("on:user-mute", { userId: user.value.id, customerIsMuted: state.customer_is_muted });
          auctionStore.$patch((storeState) => {
            if (storeState.userData[user.value.id]) {
              storeState.userData[user.value.id].customer_is_muted = state.customer_is_muted;
            }
            const index = auctionStore.activeLot?.bidders.findIndex((item) => {
              return item.id === user.value.id;
            });
            if (index && index >= 0) {
              storeState.activeLot.bidders[index].customer_is_muted = state.customer_is_muted;
            }
          });
        }
        toast.add({
          severity: response.data.status ? "success" : "error",
          summary: trans("live.toast." + (response.data.status ? "success" : "error")),
          detail: response.data.message || trans("live.toast.unknown_error"),
          life: 6000
        });
      })
      .finally(() => (state.waitIMResponse = false));
  };

  const onOpenChat = () => {
    emitter?.emit("on:user-message", user.value);
    emit("closeForm");
  };
</script>
